import React, { Component } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import "../../src/styles/login.scss";
import axios from "axios";
import LoginNavbar from "./loginNavbar";
import { getSessionEndedMessage, login } from "../utils/auth";
import { apiHost } from "../api/apiClient";
import { Helmet } from "react-helmet";
import { setErrorMessage } from "../reducers/actions";
import visionTrakLogo from "../Assets/visiontrak-logo-colored.png";
import TurnstileWidget from "./turnstileWidget";

const loginThrottleStorageKey = "assettrakLoginThrottleUntil";

class Login extends Component {

  constructor(props) {
    super(props);
    this.state = {
      username: "",
      password: "",
      rememberpwd: false,
      errorUser: false,
      errorpass: false,
      error: false,
      loginMessage: "",
      sessionEndedMessage: "",
      submitting: false,
      turnstileToken: "",
      securityCheckReady: false,
      throttleSecondsRemaining: 0,
    };

    this.turnstileWidget = React.createRef();
    this.throttleTimer = null;
    this.loginSubmissionInFlight = false;
  }

  componentDidMount() {
    const sessionEndedMessage = getSessionEndedMessage();
    if (sessionEndedMessage) {
      this.setState({ sessionEndedMessage });
    }

    const storedThrottleUntil = parseInt(localStorage.getItem(loginThrottleStorageKey), 10);
    if (storedThrottleUntil && storedThrottleUntil > Date.now()) {
      this.startThrottleCountdown(storedThrottleUntil);
    } else {
      localStorage.removeItem(loginThrottleStorageKey);
    }
  }

  componentWillUnmount() {
    if (this.throttleTimer) clearInterval(this.throttleTimer);
  }

  formatThrottleMessage = (secondsRemaining) => {
    const minutes = Math.floor(secondsRemaining / 60);
    const seconds = secondsRemaining % 60;
    return `Too many failed login attempts. Try again in ${minutes}:${seconds.toString().padStart(2, "0")} or contact a System Administrator.`;
  };

  startThrottleCountdown = (throttleUntil) => {
    if (this.throttleTimer) clearInterval(this.throttleTimer);

    const updateCountdown = () => {
      const secondsRemaining = Math.max(0, Math.ceil((throttleUntil - Date.now()) / 1000));
      if (secondsRemaining === 0) {
        clearInterval(this.throttleTimer);
        this.throttleTimer = null;
        localStorage.removeItem(loginThrottleStorageKey);
        this.setState({
          throttleSecondsRemaining: 0,
          loginMessage: "",
          turnstileToken: "",
          securityCheckReady: false,
        });
        return;
      }

      this.setState({
        throttleSecondsRemaining: secondsRemaining,
        loginMessage: this.formatThrottleMessage(secondsRemaining),
        sessionEndedMessage: "",
        submitting: false,
      });
    };

    updateCountdown();
    this.throttleTimer = setInterval(updateCountdown, 1000);
  };

  clearClientThrottle = () => {
    if (this.throttleTimer) clearInterval(this.throttleTimer);
    this.throttleTimer = null;
    localStorage.removeItem(loginThrottleStorageKey);
    this.setState({
      throttleSecondsRemaining: 0,
      loginMessage: "",
      turnstileToken: "",
      securityCheckReady: false,
    });
  };

  handleUserChange = (evt) => {
    this.setState({
      username: evt.target.value,
    });
  };

  handlePassChange = (evt) => {
    this.setState({
      password: evt.target.value,
    });
  };

  handlerememberPwd = (e) => {
    this.setState({
      rememberpwd: e.target.value
    })
  }

  handleSubmit = async (evt) => {
    var jwtDecode = require("jwt-decode");
    evt.preventDefault();
    if (this.loginSubmissionInFlight) return;
    if (this.state.throttleSecondsRemaining > 0) return;

    const validate = this.handleValidation();
    if (validate) {
      if (!this.state.turnstileToken) {
        this.setState({ loginMessage: "Please complete the security check.", sessionEndedMessage: "" });
        return;
      }

      this.loginSubmissionInFlight = true;
      let loginSucceeded = false;
      this.setState({ loginMessage: "", sessionEndedMessage: "", submitting: true });
      try {
        const response = await axios.post(`${apiHost}/api/auth/web-login`, {
          Email: this.state.username,
          Password: this.state.password,
          TurnstileToken: this.state.turnstileToken,
        });
        if (response.status == "200") {
          loginSucceeded = true;
          login(response.data.token, response.data.user.userName);
          localStorage.setItem("accountDisplayName", response.data.user.displayName || response.data.user.userName || "");
          localStorage.setItem("accountEmail", response.data.user.email || "");
          this.props.setErrorMessageAction("");
          this.setState({ loginMessage: "", sessionEndedMessage: "" });
          localStorage.removeItem("customer");
          this.props.history.push("/home");
          var token = response.data.token;

          var decoded = jwtDecode(token);
          localStorage.setItem("role", decoded.role);
          localStorage.setItem("userId", decoded.nameid);

          window.location.reload();
        }
      } catch (e) {
        if (e.response && e.response.status == 429) {
          const retryAfter = parseInt(e.response.headers && e.response.headers["retry-after"], 10);
          const throttleSeconds = retryAfter > 0 ? retryAfter : 900;
          const throttleUntil = Date.now() + throttleSeconds * 1000;
          localStorage.setItem(loginThrottleStorageKey, throttleUntil.toString());
          this.setState({ turnstileToken: "", securityCheckReady: false, submitting: false });
          if (this.turnstileWidget.current) this.turnstileWidget.current.reset();
          this.startThrottleCountdown(throttleUntil);
          return;
        }

        const loginMessage = this.getLoginErrorMessage(e);
        console.log(`😱 Axios request failed: ${e}`);
        this.setState({
          loginMessage,
          sessionEndedMessage: "",
          submitting: false,
          turnstileToken: "",
          securityCheckReady: false,
        });
        if (this.turnstileWidget.current) this.turnstileWidget.current.reset();
      } finally {
        this.loginSubmissionInFlight = false;
        if (!loginSucceeded) {
          this.setState({ submitting: false });
        }
      }
    }
  };

  getLoginErrorMessage = (error) => {
    if (!error.response) {
      return "Invalid login";
    }

    const { status, data } = error.response;

    if (typeof data === "string" && data.trim()) {
      return data;
    }

    if (status == "401") {
      return "Invalid login";
    }

    if (data && typeof data === "object") {
      return data.statusResponse || data.message || data.detail || data.title || "Invalid login";
    }

    return "Invalid login";
  };

  handleValidation = () => {
    if (!this.state.username) {
      this.setState({ errorUser: true });
      setTimeout(() => {
        this.setState({ errorUser: true });
      }, 3000);
      return false;
    }

    if (!this.state.password) {
      this.setState({ errorPass: true });
      setTimeout(() => {
        this.setState({ errorPass: true });
      }, 3000);
      return false;
    }

    return true;
  };
  render() {
    const isThrottled = this.state.throttleSecondsRemaining > 0;

    return (
      <React.Fragment>
        <Helmet>
          <title>Login </title>
        </Helmet>
        <LoginNavbar></LoginNavbar>

        <div className="loginPage">
          <video
            className="loginVideoBackground"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-hidden="true"
          >
            <source src="/videos/login-background.webm" type="video/webm" />
            <source src="/videos/login-background.mp4" type="video/mp4" />
          </video>
          <div className="loginVideoOverlay"></div>
          <div className="loginWrapper">
            <div className="loginHeader">
              <img
                className="loginLogo"
                src={visionTrakLogo}
                alt="VisionTrak"
              />
              <h1>Login.</h1>
              <p>Sign in to continue to AssetTrak.</p>
              {this.state.loginMessage && (
                <p className="error-msg">{this.state.loginMessage}</p>
              )}
              {!this.state.loginMessage && this.state.sessionEndedMessage && (
                <p className="error-msg">{this.state.sessionEndedMessage}</p>
              )}
            </div>
            <div className="loginForm">
              <form className="form" onSubmit={this.handleSubmit}>
                <div className="loginInput">
                  <div className="loginInputLeft">
                    <label>Email</label>
                  </div>
                  <div className="loginInputRight">
                    <input
                      type="text"
                      data-test="username"
                      value={this.state.username}
                      onChange={this.handleUserChange}
                      autoComplete="username"
                      disabled={isThrottled}
                    />
                    <div className="error-msg">
                      {this.state.errorUser && (
                        <p>The email field is required.</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="loginInput">
                  <div className="loginInputLeft">
                    <label>Password</label>
                  </div>
                  <div className="loginInputRight">
                    <input
                      type="password"
                      data-test="password"
                      value={this.state.password}
                      onChange={this.handlePassChange}
                      autoComplete="current-password"
                      disabled={isThrottled}
                    />
                    <div className="error-msg">
                      {this.state.errorPass && (
                        <p>The password field is required.</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="loginCheckbox">
                  <div className="loginInputLeft">
                    <input
                      type="checkbox"
                      value={this.state.rememberpwd}
                      onChange={this.handlerememberPwd}
                      disabled={isThrottled}
                    ></input>
                  </div>
                  <div className="loginInputRight">
                    <p>Remember me?</p>
                  </div>
                </div>
                {!isThrottled && (
                  <TurnstileWidget
                    ref={this.turnstileWidget}
                    action="login"
                    onVerify={(token) => this.setState({ turnstileToken: token, securityCheckReady: true, loginMessage: "" })}
                    onExpire={() => this.setState({ turnstileToken: "", securityCheckReady: false })}
                    onError={(message) => this.setState({ turnstileToken: "", securityCheckReady: false, loginMessage: message })}
                  />
                )}
                {isThrottled && (
                  <div className="loginThrottleActions">
                    <button onClick={this.clearClientThrottle} type="button">
                      Administrator cleared it? Try again now
                    </button>
                  </div>
                )}
                <div className="loginBtnContainer">
                  <div className="loginBtn">
                    <input
                      type="submit"
                      value={this.state.submitting ? "Signing in..." : "Log in"}
                      data-test="submit"
                      disabled={isThrottled || this.state.submitting || !this.state.securityCheckReady}
                    />
                  </div>
                </div>
                <div className="loginHelpLink">
                  <Link to="/forgot-password">Forgot password?</Link>
                </div>
              </form>
            </div>
            <div className="loginFooter">
                <p>&copy; {new Date().getFullYear()} - Vision Trak Rentals Pty Ltd </p>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => ({});
const mapDispatchToProps = {
  setErrorMessageAction: setErrorMessage,
};

export default connect(mapStateToProps, mapDispatchToProps)(Login);
